/**
 * Hippo header — sticky-stack scroll transition.
 *
 * Adds `.is-scrolled` to #HippoSiteTop once the user scrolls past 30px.
 * CSS handles the rest (announcement collapses, header gets shadow + blur).
 *
 * Drawer open/close is owned by Impulse's `theme.NavDrawer` (assets/theme.js).
 * Any element with `.js-drawer-open-nav` triggers it — including our mobile
 * bottom-nav Menu button. No JS wiring needed here.
 */

/** @type {HTMLElement | null} */
const siteTop = document.getElementById('HippoSiteTop');

if (siteTop) {
  const SCROLL_THRESHOLD = 30;

  const update = () => {
    siteTop.classList.toggle('is-scrolled', window.scrollY > SCROLL_THRESHOLD);
  };

  window.addEventListener('scroll', update, { passive: true });
  update();

  const setDrawerOffset = () =>
    document.documentElement.style.setProperty(
      '--hp-site-top-h',
      siteTop.offsetHeight + 'px'
    );
  new ResizeObserver(setDrawerOffset).observe(siteTop);
  setDrawerOffset();
}

// Reserve space below bottom-nav. The CSS prefers `:has(.bnav)`; this is a
// fallback for browsers without :has support (Firefox <121).
if (document.querySelector('.bnav') && !CSS.supports('selector(:has(*))')) {
  document.body.classList.add('has-bnav');
}

// Close the mobile drawer when the viewport expands to desktop width (>900px).
// theme.NavDrawer (from assets/theme.js) is initialized before any resize fires.
const desktopMql = window.matchMedia('(min-width: 901px)');
desktopMql.addEventListener('change', (mql) => {
  if (mql.matches && window.theme && theme.NavDrawer) {
    theme.NavDrawer.close();
  }
});

// Mega-menu controller — desktop only (>900px).
// Each .nav__mega-wrap trigger controls a .mega panel via aria-controls.
(function () {
  const mq = window.matchMedia('(min-width: 901px)');
  if (!mq.matches) return;

  const backdrop = document.getElementById('mega-backdrop');
  let openWrap = null;
  let closeTimer = null;
  let openTimer = null;

  function openMega(wrap) {
    clearTimeout(closeTimer);
    clearTimeout(openTimer);
    if (openWrap && openWrap !== wrap) closeMega(openWrap);
    openWrap = wrap;
    const trigger = wrap.querySelector('.nav__link--has-mega');
    const panelId = trigger && trigger.getAttribute('aria-controls');
    const panel = panelId && document.getElementById(panelId);
    wrap.classList.add('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    if (panel) {
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
    }
    if (backdrop) backdrop.classList.add('is-active');
  }

  function closeMega(wrap) {
    if (!wrap) return;
    const trigger = wrap.querySelector('.nav__link--has-mega');
    const panelId = trigger && trigger.getAttribute('aria-controls');
    const panel = panelId && document.getElementById(panelId);
    wrap.classList.remove('is-open');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
    if (panel) {
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
    }
    if (backdrop) backdrop.classList.remove('is-active');
    if (wrap === openWrap) openWrap = null;
  }

  function switchCat(panel, cat) {
    panel.querySelectorAll('.mega__cat').forEach((btn) => {
      const active = btn.dataset.cat === cat;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    panel.querySelectorAll('.mega__panel').forEach((pane) => {
      if (pane.dataset.panel === cat) {
        pane.removeAttribute('hidden');
      } else {
        pane.setAttribute('hidden', '');
      }
    });
  }

  document.querySelectorAll('.nav__mega-wrap').forEach((wrap) => {
    const trigger = wrap.querySelector('.nav__link--has-mega');
    const panelId = trigger && trigger.getAttribute('aria-controls');
    const panel = panelId && document.getElementById(panelId);

    // Hover: open after 80ms delay, close after 180ms
    wrap.addEventListener('mouseenter', () => {
      if (!mq.matches) return;
      clearTimeout(closeTimer);
      openTimer = setTimeout(() => openMega(wrap), 80);
    });
    wrap.addEventListener('mouseleave', () => {
      clearTimeout(openTimer);
      closeTimer = setTimeout(() => closeMega(wrap), 180);
    });

    if (panel) {
      panel.addEventListener('mouseenter', () => clearTimeout(closeTimer));
      panel.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => closeMega(wrap), 180);
      });

      // Category switching
      panel.querySelectorAll('.mega__cat').forEach((btn) => {
        btn.addEventListener('mouseenter', () => switchCat(panel, btn.dataset.cat));
        btn.addEventListener('focus', () => switchCat(panel, btn.dataset.cat));
      });
    }

    // Click toggle
    if (trigger) {
      trigger.addEventListener('click', (e) => {
        if (!mq.matches) return;
        e.preventDefault();
        if (wrap.classList.contains('is-open')) {
          closeMega(wrap);
        } else {
          openMega(wrap);
        }
      });
    }
  });

  // Escape closes + returns focus to trigger
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && openWrap) {
      const trigger = openWrap.querySelector('.nav__link--has-mega');
      closeMega(openWrap);
      if (trigger) trigger.focus();
    }
  });

  // Backdrop click closes
  if (backdrop) {
    backdrop.addEventListener('click', () => {
      if (openWrap) closeMega(openWrap);
    });
  }
})();
